// HTTP client of the student-requests module (student + admin endpoints).
import axios from '@/config/axios.config';
import {
  StudentRequest,
  AttestationTypeOption,
  EligibilityCheckResult,
  CreateRequestDto,
  ValidateRequestDto,
  RequestFilterDto,
  PaginatedRequests,
  PaginatedAdminRequests,
  AdminListParams,
  AttestationType,
  EnrolledYear,
  AttestationConfigItem,
  UpdateAttestationConfigDto,
  DownloadedDocument,
} from '../types/student-requests.types';

const API_BASE_URL = '/api/student-requests';
const ADMIN_API_BASE_URL = '/api/admin/student-requests';

/** File name from a Content-Disposition header (RFC 5987 or plain), or null. */
export const filenameFromDisposition = (header: string | undefined | null): string | null => {
  if (!header) return null;
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf8?.[1]) {
    try {
      return decodeURIComponent(utf8[1].trim());
    } catch {
      return utf8[1].trim();
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain?.[1]?.trim() || null;
};

// ==================== STUDENT ENDPOINTS ====================

export const getAvailableTypes = async (academicYearId?: string): Promise<AttestationTypeOption[]> => {
  const response = await axios.get(`${API_BASE_URL}/types`, {
    params: academicYearId ? { academicYearId } : undefined,
  });
  return response.data;
};

export const getEnrolledYears = async (): Promise<EnrolledYear[]> => {
  const response = await axios.get(`${API_BASE_URL}/years`);
  return response.data;
};

export const checkEligibility = async (
  type: AttestationType,
  academicYearId?: string,
): Promise<EligibilityCheckResult> => {
  const response = await axios.get(`${API_BASE_URL}/eligibility/${type}`, {
    params: academicYearId ? { academicYearId } : undefined,
  });
  return response.data;
};

export const createRequest = async (
  dto: CreateRequestDto,
): Promise<{ id: string; referenceNumber: string; status: string; createdAt: string }> => {
  const response = await axios.post(API_BASE_URL, dto);
  return response.data;
};

export const getMyRequests = async (filters?: RequestFilterDto): Promise<PaginatedRequests> => {
  const response = await axios.get(API_BASE_URL, { params: filters });
  return response.data;
};

export const getRequestById = async (id: string): Promise<StudentRequest> => {
  const response = await axios.get(`${API_BASE_URL}/${id}`);
  return response.data;
};

export const cancelRequest = async (id: string): Promise<{ success: boolean }> => {
  const response = await axios.patch(`${API_BASE_URL}/${id}/cancel`);
  return response.data;
};

// ==================== ADMIN ENDPOINTS ====================

const adminList = async (path: string, params?: AdminListParams): Promise<PaginatedAdminRequests> => {
  const response = await axios.get(`${ADMIN_API_BASE_URL}/${path}`, {
    params: {
      ...(params?.academicYearId ? { academicYearId: params.academicYearId } : {}),
      page: params?.page ?? 1,
      limit: params?.limit ?? 20,
    },
  });
  return response.data;
};

export const getPendingFinanceRequests = (params?: AdminListParams): Promise<PaginatedAdminRequests> =>
  adminList('pending/finance', params);

export const getPendingAcademicRequests = (params?: AdminListParams): Promise<PaginatedAdminRequests> =>
  adminList('pending/academic', params);

export const getAllRequests = (params?: AdminListParams): Promise<PaginatedAdminRequests> =>
  adminList('all', params);

export const getApprovedRequests = (params?: AdminListParams): Promise<PaginatedAdminRequests> =>
  adminList('approved', params);

export const getPendingOtherYearsCount = async (academicYearId: string): Promise<{ count: number }> => {
  const response = await axios.get(`${ADMIN_API_BASE_URL}/pending-other-years`, {
    params: { academicYearId },
  });
  return response.data;
};

export const validateFinance = async (id: string, dto: ValidateRequestDto): Promise<{ success: boolean }> => {
  const response = await axios.patch(`${ADMIN_API_BASE_URL}/${id}/validate/finance`, dto);
  return response.data;
};

export const validateAcademic = async (id: string, dto: ValidateRequestDto): Promise<{ success: boolean }> => {
  const response = await axios.patch(`${ADMIN_API_BASE_URL}/${id}/validate/academic`, dto);
  return response.data;
};

export const markAsReadyToCollect = async (id: string): Promise<{ success: boolean }> => {
  const response = await axios.patch(`${ADMIN_API_BASE_URL}/${id}/ready-to-collect`);
  return response.data;
};

/**
 * Downloads the document for printing. The file name comes from the server
 * (a convention de stage may be a .docx), with a PDF fallback.
 */
export const downloadForPrint = async (id: string, referenceNumber?: string): Promise<DownloadedDocument> => {
  const response = await axios.get(`${ADMIN_API_BASE_URL}/${id}/download-print`, {
    responseType: 'blob',
  });
  const disposition = (response.headers as Record<string, string | undefined>)['content-disposition'];
  const filename = filenameFromDisposition(disposition) ?? `attestation-${referenceNumber ?? id}.pdf`;
  const contentType = (response.headers as Record<string, string | undefined>)['content-type'];
  const raw = response.data as Blob;
  const blob = raw.type || !contentType ? raw : new Blob([raw], { type: contentType });
  return { blob, filename };
};

export const markAsPrinted = async (id: string): Promise<{ success: boolean }> => {
  const response = await axios.patch(`${ADMIN_API_BASE_URL}/${id}/printed`);
  return response.data;
};

export const markAsDelivered = async (id: string, deliveryNotes?: string): Promise<{ success: boolean }> => {
  const response = await axios.patch(`${ADMIN_API_BASE_URL}/${id}/delivered`, {
    deliveryNotes: deliveryNotes?.trim() ? deliveryNotes.trim() : undefined,
  });
  return response.data;
};

// ==================== ADMIN CONFIGURATION ====================

export const getAttestationConfigs = async (): Promise<AttestationConfigItem[]> => {
  const response = await axios.get(`${ADMIN_API_BASE_URL}/config`);
  return response.data;
};

export const updateAttestationConfig = async (
  type: AttestationType,
  dto: UpdateAttestationConfigDto,
): Promise<AttestationConfigItem> => {
  const response = await axios.put(`${ADMIN_API_BASE_URL}/config/${type}`, dto);
  return response.data;
};
